import { useSelector } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { CardJobSummary, OutputFormat } from '../../../api/cards';
import {
  cardJobsQueryOptions,
  getCardDownloadUrl,
  sourceForType,
  useCardTemplates,
} from '../../../api/cards';
import { useIseeEnabled, useIseeSessions } from '../../../api/isee';
import type { CardsSearch, CardsSearchPatch } from '../cards-url-state';
import { CARDS_SEARCH_CLEAR } from '../cards-url-state';
import { cleanQuery, toSessionQuery } from '../isee-filters';
import type { CardGenFields, DerivedBatch } from './card-batch';
import {
  buildBatchPreflightInput,
  deriveBatch,
  detectCsvDelimiter,
  effectiveSortBy,
  hasRecipientsFor,
  parseCsvPreview,
  selectedTplFor,
} from './card-batch';
import { CardGenStepper } from './CardGenStepper';
import { DownloadStep } from './DownloadStep';
import { GeneratorForm } from './GeneratorForm';
import { PendingReviewBanner } from './PendingReviewBanner';
import { RecapPanel } from './RecapPanel';
import { BusyNotice, RunningStep } from './RunningStep';
import {
  useCardBatchRun,
  useCardGenForm,
  useCardTemplatePick,
} from './use-card-batch';

interface CardGeneratorProps {
  /** URL-persisted form state, read once at mount to pre-fill the form. */
  search?: CardsSearch;
  /** Write a patch back to the URL; an `undefined` value removes its key. */
  onSearchPatch?: (patch: CardsSearchPatch) => void;
}

export function CardGenerator({
  search,
  onSearchPatch,
}: CardGeneratorProps = {}) {
  const savedTemplates = useCardTemplates();
  // Everything the operator picks lives in one form; the batch lifecycle
  // below (phase, progress, results) is not form state and stays in useState.
  const form = useCardGenForm(search, () => void handlePreflightRef.current());
  // Only what the stepper below reacts to. Everything else the form holds is
  // read at submit time from `form.state.values`, so a keystroke no longer
  // re-renders this component — and with it the whole form.
  const hasTemplate = useSelector(
    form.store,
    (state) => !!state.values.templateId || !!state.values.templateFile,
  );
  const outputFormat = useSelector(
    form.store,
    (state) => state.values.outputFormat,
  );

  const [csvRaw, setCsvRaw] = useState<string | null>(null);

  // The batch's life — pre-flight, recap, commit, render, the SSE stream —
  // is not form state: it describes what the server is doing.
  const batch = useCardBatchRun({ search, onSearchPatch });
  const {
    phase,
    progress,
    results,
    jobId,
    recap,
    error,
    throttled,
    finalizing,
  } = batch;

  // Pre-flight batches awaiting review (recap ready, never committed), so the
  // flow can be re-entered from a fresh tab without the resume URL. Fetched
  // only while the form is idle and no batch is being resumed — react-query
  // owns the request's lifecycle, so there is no in-flight race to guard.
  const { data: jobs } = useQuery({
    ...cardJobsQueryOptions(),
    enabled: phase === 'idle' && !search?.resume && !search?.job,
  });
  const pendingReviews: CardJobSummary[] = (jobs ?? []).filter(
    (j) => j.kind === 'resolve' && j.status === 'review',
  );

  // Set below, once handlePreflight exists; read by the form's onSubmit.
  const handlePreflightRef = useRef<() => Promise<void>>(() =>
    Promise.resolve(),
  );

  const template = useCardTemplatePick({
    form,
    search,
    savedTemplates,
    onSearchPatch,
  });

  // Parsed once here rather than inside deriveBatch, so splitting a large CSV
  // is not redone every time an unrelated field changes.
  const csvDelimiter = useSelector(
    form.store,
    (state) => state.values.delimiter,
  );
  const csv = useMemo(
    () => (csvRaw === null ? null : parseCsvPreview(csvRaw, csvDelimiter)),
    [csvRaw, csvDelimiter],
  );

  // The ISEE selection is read as the picker shows it: cut down to what the
  // sessions tree (cached, loaded by the recipients step) still offers, so a
  // stale cohort restored from the URL is never what gets submitted.
  const iseeTree = useIseeSessions(useIseeEnabled()).tree;
  const withCleanIsee = useCallback(
    (values: CardGenFields): CardGenFields => ({
      ...values,
      iseeQuery: cleanQuery(iseeTree, values.iseeQuery),
    }),
    [iseeTree],
  );

  /** Everything the current values add up to. Pure, so it is safe to call in a
   *  handler as well as in a render. */
  const derive = useCallback(
    () =>
      deriveBatch(
        withCleanIsee(form.state.values),
        template,
        savedTemplates,
        csv,
      ),
    [form, withCleanIsee, template, savedTemplates, csv],
  );

  // Publish flow state to the parent stepper. `hasRecipients` is the one
  // derived value needed reactively rather than at submit time — computed from
  // its three inputs directly, so the full derivation does not run per event.
  const hasRecipients = useSelector(form.store, (state) =>
    hasRecipientsFor(
      withCleanIsee(state.values),
      sourceForType(
        selectedTplFor(state.values.templateId, savedTemplates)?.type,
      ),
    ),
  );

  // CSV upload: read it and auto-detect the delimiter.
  const handleCsvFile = useCallback(
    async (f: File) => {
      form.setFieldValue('csvFile', f);
      const text = await f.text();
      setCsvRaw(text);
      form.setFieldValue('delimiter', detectCsvDelimiter(text));
      form.setFieldValue('delimiterIsAuto', true);
    },
    [form],
  );

  // Phase 1 — pre-flight. Build the request for the active source and let the
  // batch hook post it and stream the resolve progress.
  const handlePreflight = useCallback(async () => {
    const values = withCleanIsee(form.state.values);
    const d = derive();
    if (!values.templateFile) {
      return;
    }
    if (!hasSourceInputs(values, d.source)) {
      return;
    }

    await batch.startPreflight(
      buildBatchPreflightInput({
        source: d.source,
        templateId: values.templateId,
        templateFile: values.templateFile,
        csvFile: values.csvFile,
        delimiter: values.delimiter,
        outputFormat: values.outputFormat,
        sides: d.effectiveSides,
        sortBy: effectiveSortBy(values.sortBy, d.sortFields),
        iseeQuery: values.iseeQuery,
        logins: values.logins,
        formFields: d.formFields,
        values: values.values,
      }),
    );
  }, [batch, derive, form, withCleanIsee]);

  // `onSubmit` is captured by useForm on the first render, so it calls through
  // a ref rather than closing over this render's handlePreflight — which reads
  // the derived view of the fields (effective sides, the `form:` subset
  // actually needed) and is rebuilt whenever any of that changes.
  useLayoutEffect(() => {
    handlePreflightRef.current = handlePreflight;
  });

  const handleDownloadResult = async () => {
    if (!jobId) {
      return;
    }
    await downloadBatchResult(jobId, outputFormat);
  };

  const hasSavedFormState = searchHasFormState(search);

  // Full reset: the lifecycle plus the form itself (and its URL state).
  const handleReset = () => {
    batch.newBatch();
    onSearchPatch?.(CARDS_SEARCH_CLEAR);
    form.setFieldValue('iseeQuery', {});
    form.setFieldValue('logins', []);
    form.setFieldValue('csvFile', null);
    setCsvRaw(null);
    form.setFieldValue('delimiter', ',');
    form.setFieldValue('delimiterIsAuto', true);
    template.clear();
    form.setFieldValue('outputFormat', 'pdf');
    form.setFieldValue('sides', 'front');
  };

  // The stepper is rendered here, beside the state it reflects, rather than
  // by the page from a copy this component would have to push up to it.
  return (
    <>
      <CardGenStepper
        hasTemplate={hasTemplate}
        hasRecipients={hasRecipients}
        phase={phase}
      />
      <section className="epi-card tone-blue">
        {phase === 'idle' && pendingReviews.length > 0 && (
          <PendingReviewBanner
            jobs={pendingReviews}
            onResume={(id) => void batch.resumeBatch(id)}
          />
        )}
        {(phase === 'idle' || phase === 'error') && (
          <GeneratorForm
            form={form}
            template={template}
            csv={csv}
            search={search}
            error={error}
            onCsvFile={(f) => void handleCsvFile(f)}
            onSubmit={() => void form.handleSubmit()}
            onSearchPatch={onSearchPatch}
          />
        )}

        {(phase === 'uploading' || phase === 'committing') && (
          <BusyNotice phase={phase} />
        )}

        {(phase === 'processing' || phase === 'preflighting') && (
          <RunningStep
            phase={phase}
            progress={progress}
            throttled={throttled}
            finalizing={finalizing}
            results={results}
            jobId={jobId}
            onCancel={batch.cancelRun}
          />
        )}

        {phase === 'recap' && recap && (
          <RecapPanel
            recap={recap}
            canAdjust={hasSavedFormState}
            onBack={() => {
              // Adjusting means this pre-flight is dead: discard it server-side
              // so it stops showing as "awaiting review", keep the filled form.
              batch.discardCurrent();
              batch.newBatch();
            }}
            onCancel={() => {
              batch.discardCurrent();
              handleReset();
            }}
            onContinue={() => {
              void batch.commit(outputFormat);
            }}
          />
        )}

        {phase === 'done' && (
          <DownloadStep
            outputFormat={outputFormat}
            results={results}
            jobId={jobId}
            phase={phase}
            deduplicated={batch.deduplicated}
            onDownload={() => void handleDownloadResult()}
            onNewBatch={batch.newBatch}
          />
        )}
      </section>
    </>
  );
}

/** Whether the recipient list the active source reads from has been given. */
function hasSourceInputs(
  values: CardGenFields,
  source: DerivedBatch['source'],
): boolean {
  if (source === 'isee' && !toSessionQuery(values.iseeQuery)) {
    return false;
  }
  if (source === 'lucca' && !(values.templateId && values.logins.length)) {
    return false;
  }
  if (source === 'csv' && !values.csvFile) {
    return false;
  }
  return true;
}

async function downloadBatchResult(jobId: string, outputFormat: OutputFormat) {
  try {
    const url = await getCardDownloadUrl(jobId, outputFormat);
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    a.click();
  } catch {
    /* noop */
  }
}

// "Adjust selection" only makes sense when the URL still carries form state
// to go back to — a resume in a fresh tab (or a CSV batch, whose file can't
// be URL-persisted) would land on an empty form.
function searchHasFormState(search: CardsSearch | undefined): boolean {
  return Boolean(
    search &&
    (search.template ||
      search.cohort ||
      search.logins?.length ||
      (search.values && Object.keys(search.values).length > 0)),
  );
}

/**
 * Pre-flight recap shown before generation. Summarises how many recipients
 * passed the check and lists those that will be skipped (with the reason), then
 * lets the user go back to the filled form to adjust it, cancel (reset the
 * form) or continue (render the valid ones).
 */
